import * as THREE from "three";
import GUI from "lil-gui";
import gsap from "gsap";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import holographicVertexShader from "./shaders/holographic/vertex.glsl";
import holographicFragmentShader from "./shaders/holographic/fragment.glsl";
import gridGlowFragmentShader from "./shaders/grid/gridGlowFragmentShader.glsl";
import gridGlowVertexShader from "./shaders/grid/gridGlowVertexShader.glsl";

document.addEventListener("DOMContentLoaded", () => {
	const backgroundMusic = document.getElementById("backgroundMusic");
	const audioControlBtn = document.getElementById("audio-control");
	const audioControlIcon = audioControlBtn.querySelector("i");

	backgroundMusic.volume = 0.5;

	let isPlaying = false;

	// Function to toggle audio playback and update the button icon
	const toggleAudioPlayback = () => {
		if (isPlaying) {
			backgroundMusic.pause();
			audioControlIcon.className = "bi bi-volume-mute";
		} else {
			backgroundMusic.play();
			audioControlIcon.className = "bi bi-volume-up";
		}
		isPlaying = !isPlaying;
	};

	audioControlBtn.addEventListener("click", toggleAudioPlayback);

	// Function to set up button click sounds
	const setupButtonSounds = () => {
		const startSound = document.getElementById("startSound");
		const exitViewSound = document.getElementById("exitViewSound");
		const nextModelSound = document.getElementById("nextModelSound");
		const prevModelSound = document.getElementById("prevModelSound");
		const moreInfoSound = document.getElementById("moreInfoSound");

		const startsceneBtn = document.getElementById("start-scene-btn");
		const exitViewBtn = document.getElementById("exit-view");
		const nextModelBtn = document.getElementById("next-model");
		const prevModelBtn = document.getElementById("prev-model");
		const moreInfoBtn = document.getElementById("more-info");

		const playSound = (sound) => {
			sound.currentTime = 0;
			sound.play();
		};

		startsceneBtn.addEventListener("click", () => playSound(startSound));
		exitViewBtn.addEventListener("click", () => playSound(exitViewSound));
		nextModelBtn.addEventListener("click", () => playSound(nextModelSound));
		prevModelBtn.addEventListener("click", () => playSound(prevModelSound));
		moreInfoBtn.addEventListener("click", () => playSound(moreInfoSound));
	};

	setupButtonSounds();
});

/**
 * Loaders
 */
const loadingBarElement = document.querySelector(".loading-bar");
const loadingManager = new THREE.LoadingManager(
	() => {
		// Loading complete
		window.setTimeout(() => {
			// Update the loading bar element
			loadingBarElement.classList.add("ended");
			loadingBarElement.style.transform = "";

			const startSceneContainer = document.getElementById(
				"start-scene-container",
			);
			const moreInfoOverlay = document.getElementById("more-info-overlay");
			const backgroundOverlay = document.getElementById("background-overlay");

			startSceneContainer.style.display = "block"; // Show the start button
			backgroundOverlay.style.display = "block"; // Show the background overlay

			// Hide the overlays when the start scene button is clicked
			const startSceneBtn = document.getElementById("start-scene-btn");
			startSceneBtn.addEventListener("click", () => {
				startSceneContainer.style.display = "none"; // Hide start button
				moreInfoOverlay.style.display = "none"; // Hide info overlay
				backgroundOverlay.style.display = "none"; // Hide background overlay

				animateScene(); // Initialize and render the scene
			});

			// Show the info overlay when loading is finished
			moreInfoOverlay.style.display = "block";
		}, 500);
	},
	(itemUrl, itemsLoaded, itemsTotal) => {
		const progressRatio = itemsLoaded / itemsTotal;
		loadingBarElement.style.transform = `scaleX(${progressRatio})`;
	},
);

// Manage the visibility of the more-info overlay and toggle button icon
document.getElementById("more-info").addEventListener("click", () => {
	const moreInfoOverlay = document.getElementById("more-info-overlay");
	const moreInfoBtn = document.getElementById("more-info");
	const moreInfoIcon = moreInfoBtn.querySelector("i");

	const isOverlayVisible = moreInfoOverlay.style.display === "block";

	// Toggle overlay visibility
	moreInfoOverlay.style.display = isOverlayVisible ? "none" : "block";

	// Toggle button icon
	if (isOverlayVisible) {
		moreInfoIcon.className = "bi bi-info-lg";
	} else {
		moreInfoIcon.className = "bi bi-x-lg";
	}
});

let sceneReady = false; // Flag to track if the scene is ready for interaction

function animateScene() {
	const moreInfoOverlay = document.getElementById("more-info-overlay");
	moreInfoOverlay.style.display = "none"; // Hide the more-info overlay when the scene starts

	gsap.to(overlayMaterial.uniforms.uAlpha, { duration: 5, value: 0, delay: 1 });
	gsap.to(camera.position, {
		duration: 1,
		delay: 0.1,
		x: 0,
		y: 190,
		z: 0,
		onUpdate: () => {
			controls.enabled = false;
			controls.enableDamping = false;
		},
	});
	gsap.to(camera.position, {
		duration: 3.5,
		delay: 2.5,
		ease: "power2.out",
		x: 0,
		y: 15,
		z: 0,
	});
	gsap.to(controls.target, {
		duration: 3.5,
		delay: 0.1,
		ease: "power1.out",
		x: -0.1,
		y: -5,
		z: -0.1,
	});
	gsap.to(camera.position, {
		duration: 3.5,
		delay: 5.5,
		ease: "power1.inOut",
		x: 20,
		y: 25,
		z: 20,
		onComplete: () => {
			hideModelUI();
			controls.enabled = true;
			controls.enablePan = true;
			controls.panSpeed = 0.75;
			controls.enableDamping = true;
			controls.dampingFactor = 0.025;
			controls.maxTargetRadius = 75;
			controls.enableRotate = false;
			controls.enableZoom = false;
			controls.zoomToCursor = false;
			controls.screenSpacePanning = false;

			// Mouse controls
			controls.mouseButtons = {
				LEFT: THREE.MOUSE.PAN,
				RIGHT: THREE.MOUSE.ROTATE,
			};
			// Touch controls
			controls.touches = {
				ONE: THREE.TOUCH.PAN,
				TWO: THREE.TOUCH.ROTATE,
			};
			enableKeyboardControls(); // Re-enable keyboard controls here
			controls.update();

			// Set sceneReady flag to true and add the click listener
			sceneReady = true;
		},
	});
	gsap.to(controls.target, {
		duration: 1,
		delay: 5.25,
		ease: "power1.out",
		x: 0,
		y: 0,
		z: 0,
	});
}

// Texture loader
const textureLoader = new THREE.TextureLoader(loadingManager);

// Draco loader
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("draco/");

// GLTF loader
const gltfLoader = new GLTFLoader(loadingManager);
gltfLoader.setDRACOLoader(dracoLoader);

/**
 * Base
 */

/**
 * Debug
 */
const gui = new GUI({
	width: 300,
	title: "GUI Debug UI",
});
gui.hide();
window.addEventListener("keydown", (event) => {
	if (event.key == "h") gui.show(gui._hidden);
});

const debugObject = {
	// Main color palette
	threeToon01Color: "#fbeac7",
	threeToon02Color: "#95B0AE",
	threeToon03Color: "#ef9782",
	threeToon04Color: "#e55f34",
	threeToon05Color: "#f78648",
	threeToon06Color: "#feb81c",
	threeToon07Color: "#bb972f",
	threeToon08Color: "#82b690",
	threeToon09Color: "#4582a2",
	threeToon10Color: "#2a2925",
	threeToon11Color: "#fffef3",
	threeToon12Color: "#9e9a9a",

	fogColor: "#95B0AE",
	rendererColor: "#95B0AE",

	holoColor: "#0091ff",
	glowColor: "#feb81c",

	// fiveToon
	fiveToon01Color: "#ffffff",
};

// GUI Main Color Palette
const mainPalette = gui.addFolder("Main Color Palette");

mainPalette.addColor(debugObject, "threeToon01Color").onChange(() => {
	threeToon01.color.set(debugObject.threeToon01Color);
});
mainPalette.addColor(debugObject, "threeToon02Color").onChange(() => {
	threeToon02.color.set(debugObject.threeToon02Color);
});
mainPalette.addColor(debugObject, "threeToon03Color").onChange(() => {
	threeToon03.color.set(debugObject.threeToon03Color);
});
mainPalette.addColor(debugObject, "threeToon04Color").onChange(() => {
	threeToon04.color.set(debugObject.threeToon04Color);
});
mainPalette.addColor(debugObject, "threeToon05Color").onChange(() => {
	threeToon05.color.set(debugObject.threeToon05Color);
});
mainPalette.addColor(debugObject, "threeToon06Color").onChange(() => {
	threeToon06.color.set(debugObject.threeToon06Color);
});
mainPalette.addColor(debugObject, "threeToon07Color").onChange(() => {
	threeToon07.color.set(debugObject.threeToon07Color);
});
mainPalette.addColor(debugObject, "threeToon08Color").onChange(() => {
	threeToon08.color.set(debugObject.threeToon08Color);
});
mainPalette.addColor(debugObject, "threeToon09Color").onChange(() => {
	threeToon09.color.set(debugObject.threeToon09Color);
});
mainPalette.addColor(debugObject, "threeToon10Color").onChange(() => {
	threeToon10.color.set(debugObject.threeToon10Color);
});
mainPalette.addColor(debugObject, "threeToon11Color").onChange(() => {
	threeToon11.color.set(debugObject.threeToon11Color);
});
mainPalette.addColor(debugObject, "threeToon12Color").onChange(() => {
	threeToon12.color.set(debugObject.threeToon12Color);
});

// GUI Renderer Color Palette
const rendererPalette = gui.addFolder("Renderer Color Palette");
rendererPalette.addColor(debugObject, "fogColor").onChange(() => {
	fog.color.set(debugObject.fogColor);
});
rendererPalette.addColor(debugObject, "rendererColor").onChange(() => {
	renderer.setClearColor(rendererColor);
});

const shaderPalette = gui.addFolder("Shader Color Palette");

shaderPalette.addColor(debugObject, "holoColor").onChange(() => {
	holoMaterial.uniforms.uColor.value.set(debugObject.holoColor);
});
shaderPalette.addColor(debugObject, "glowColor").onChange(() => {
	glowMaterial.uniforms.uColor.value.set(debugObject.glowColor);
});

/**
 * Declare Global Variable
 */
let mixers = [];
let sceneMixer;
let flagMixer;
let actions = []; // This can also be a Map if you prefer
let sceneWheelModel01, sceneWheelModel02, sceneWheelModel03;
let wheelModels = []; // Array to store all wheel models

const MODEL_INDEX = {
	MAIN: 0,
	BIRD: 1,
	CAREN: 2,
	CRANK: 3,
	MARBLE: 4,
	NORIA: 5,
	ALJAZARI: 6,
	SOLAR: 7,
};

const predefinedCameraPositions = [
	{
		position: new THREE.Vector3(10, 5, 10),
		lookAt: new THREE.Vector3(-5, 0, 0),
	}, // Model 01
	{
		position: new THREE.Vector3(4, 7, -68),
		lookAt: new THREE.Vector3(-2.5, 4, -71.5),
	}, // Model 02
	{
		position: new THREE.Vector3(30, 2.5, -12.5),
		lookAt: new THREE.Vector3(50, 7.5, -30),
	}, // Model 03
	{
		position: new THREE.Vector3(65, 5, 40),
		lookAt: new THREE.Vector3(60, 3, 24),
	}, // Model 04
	{
		position: new THREE.Vector3(27, 5.5, 62.5),
		lookAt: new THREE.Vector3(18.5, 5, 58.5),
	}, // Model 05
	{
		position: new THREE.Vector3(-15, 3, 80),
		lookAt: new THREE.Vector3(-30, 3, 68),
	}, // Model 06
	{
		position: new THREE.Vector3(-40, 9, 22),
		lookAt: new THREE.Vector3(-59, 4, 24),
	}, // Model 07
	{
		position: new THREE.Vector3(-40, 3.5, -30),
		lookAt: new THREE.Vector3(-57, 3.5, -28),
	}, // Model 08
];

const modelExitViews = predefinedCameraPositions.map((model) => {
	const refVector = new THREE.Vector3(20, 25, 20); // Computed as shown above
	const exitLookAt = model.lookAt;
	const exitPosition = new THREE.Vector3().addVectors(exitLookAt, refVector);
	return { exitPosition, exitLookAt };
});

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();

/**
 * Overlay
 */
const overlayGeometry = new THREE.PlaneGeometry(2, 2, 1, 1);
const overlayMaterial = new THREE.ShaderMaterial({
	// wireframe: true,
	transparent: true,
	uniforms: {
		uAlpha: { value: 1 }, // change to 1 when need loader
	},
	vertexShader: `
        void main()
        {
            gl_Position = vec4(position, 1);
        }
    `,
	fragmentShader: `
        uniform float uAlpha;

        void main()
        {
            gl_FragColor = vec4(0.584,0.69,0.682, uAlpha);
        }
    `,
});
const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial);
scene.add(overlay);

/**
 * Textures
 */
const matcap00Texture = textureLoader.load(
	"./textures/matcaps/matcap-create-00.png",
);
matcap00Texture.colorSpace = THREE.SRGBColorSpace;

const waterNormalsTexture = textureLoader.load(
	"./textures/waternormals.jpg",
	function (texture) {
		texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
	},
);

const gradient2LightTexture = textureLoader.load(
	"./textures/gradients/2-light.jpg",
);

const gradient2DarkTexture = textureLoader.load(
	"./textures/gradients/2-dark.jpg",
);

const gradient3Texture = textureLoader.load("./textures/gradients/3.jpg");
gradient3Texture.minFilter = THREE.NearestFilter;
gradient3Texture.magFilter = THREE.NearestFilter;
gradient3Texture.generateMipmaps = false;

const gradient5Texture = textureLoader.load("./textures/gradients/5.jpg");
gradient5Texture.minFilter = THREE.NearestFilter;
gradient5Texture.magFilter = THREE.NearestFilter;
gradient5Texture.generateMipmaps = false;

const flag01Texture = textureLoader.load("./textures/flag/flag01.png");

flag01Texture.colorSpace = THREE.SRGBColorSpace;

/**
 * Materials
 */

const flag01Material = new THREE.MeshToonMaterial({ map: flag01Texture });

// MeshMatcapMaterial
const matcap00Material = new THREE.MeshMatcapMaterial({
	matcap: matcap00Texture,
});

// Pole light material
const poleLightMaterial = new THREE.MeshBasicMaterial({ color: 0xffffe5 });

// MeshToonMaterial

const threeToon01 = new THREE.MeshToonMaterial();
threeToon01.color.set(0xfbeac7);
threeToon01.side = THREE.DoubleSide;
threeToon01.gradientMap = gradient3Texture;

const threeToon02 = new THREE.MeshToonMaterial();
threeToon02.color.set(0x95b0ae);
threeToon02.side = THREE.DoubleSide;
threeToon02.gradientMap = gradient3Texture;

const threeToon03 = new THREE.MeshToonMaterial();
threeToon03.color.set(0xef9782);
threeToon03.side = THREE.DoubleSide;
threeToon03.gradientMap = gradient3Texture;

const threeToon04 = new THREE.MeshToonMaterial();
threeToon04.color.set(0xe55f34);
threeToon04.side = THREE.DoubleSide;
threeToon04.gradientMap = gradient3Texture;

const threeToon05 = new THREE.MeshToonMaterial();
threeToon05.color.set(0xf78648);
threeToon05.side = THREE.DoubleSide;
threeToon05.gradientMap = gradient3Texture;

const threeToon06 = new THREE.MeshToonMaterial();
threeToon06.color.set(0xfeb81c);
threeToon06.side = THREE.DoubleSide;
threeToon06.gradientMap = gradient3Texture;

const threeToon07 = new THREE.MeshToonMaterial();
threeToon07.color.set(0xbb972f);
threeToon07.side = THREE.DoubleSide;
threeToon07.gradientMap = gradient3Texture;

const threeToon08 = new THREE.MeshToonMaterial();
threeToon08.color.set(0x82b690);
threeToon08.side = THREE.DoubleSide;
threeToon08.gradientMap = gradient3Texture;

const threeToon08Water = new THREE.MeshStandardMaterial({
	color: 0x82b690, // Set the color using hexadecimal
	side: THREE.DoubleSide, // Render both sides of the material
	normalMap: waterNormalsTexture, // Set the loaded and configured water normals texture
	metalness: 0.5, // Value between 0 (non-metallic) and 1 (metallic)
	roughness: 0.3, // Value between 0 (smooth, glossy) and 1 (rough, matte)
});

const threeToon09 = new THREE.MeshToonMaterial();
threeToon09.color.set(0x4582a2);
threeToon09.side = THREE.DoubleSide;
threeToon09.gradientMap = gradient3Texture;

const threeToon10 = new THREE.MeshToonMaterial();
threeToon10.color.set(0x2a2925);
threeToon10.side = THREE.DoubleSide;
threeToon10.gradientMap = gradient3Texture;

const threeToon11 = new THREE.MeshToonMaterial();
threeToon11.color.set(0xfffef3);
threeToon11.side = THREE.DoubleSide;
threeToon11.gradientMap = gradient3Texture;

const threeToon12 = new THREE.MeshToonMaterial();
threeToon12.color.set(0x9e9a9a);
threeToon12.side = THREE.DoubleSide;
threeToon12.gradientMap = gradient3Texture;

const fiveToon = new THREE.MeshToonMaterial();
fiveToon.side = THREE.DoubleSide;
fiveToon.gradientMap = gradient5Texture;

/* *
 * Lights
 */

// AmbientLight
const ambientLight = new THREE.AmbientLight(0xfbeac7, 0.5);
scene.add(ambientLight);

// PointLight 01
const pointLight1 = new THREE.PointLight(0xfbeac7, 100, 0, 2.25);
pointLight1.position.set(0, 5, 2.5);
scene.add(pointLight1);

// PointLight 02
// const pointLight2 = new THREE.PointLight(0xfbeac7, 20, 0, 2.25)
// pointLight2.position.set( -0.5, 4.4, -72)
// pointLight2.castShadow = true
// pointLight2.shadow.mapSize.width = 512
// pointLight2.shadow.mapSize.height = 512
// pointLight2.shadow.radius = 5
// pointLight2.shadow.camera.near = 0.1
// pointLight2.shadow.camera.far = 15
// scene.add(pointLight2)

// PointLight 03
const pointLight3 = new THREE.PointLight(0xfbeac7, 100, 0, 2.25);
pointLight3.position.set(53, 5, -30);
scene.add(pointLight3);

// PointLight 04
const pointLight4 = new THREE.PointLight(0xfbeac7, 100, 0, 2.25);
pointLight4.position.set(62, 5, 23);
scene.add(pointLight4);

// PointLight 05
const pointLight5 = new THREE.PointLight(0xfbeac7, 100, 0, 2.25);
pointLight5.position.set(22, 9.5, 58);
scene.add(pointLight5);

// PointLight 06
const pointLight6 = new THREE.PointLight(0xfbeac7, 100, 0, 2.25);
pointLight6.position.set(-30, 5, 68);
scene.add(pointLight6);

// PointLight 07
const pointLight7 = new THREE.PointLight(0xfbeac7, 100, 0, 2.25);
pointLight7.position.set(-60, 5, 22);
scene.add(pointLight7);

// PointLight 08
const pointLight8 = new THREE.PointLight(0xfbeac7, 100, 0, 2.25);
pointLight8.position.set(-57, 5, -33);
scene.add(pointLight8);

// Main DirectionalLight

const dirLight = new THREE.DirectionalLight(0xfbeac7, 1.75);
dirLight.scale.set(10, 1, 1);
// dirLight.color.setHSL( 1, 1, 1 )
dirLight.position.set(0, 20, 85);
scene.add(dirLight);

dirLight.castShadow = true;

dirLight.shadow.mapSize.width = 1024;
dirLight.shadow.mapSize.height = 1024;

const d = 90;

dirLight.shadow.camera.left = -d;
dirLight.shadow.camera.right = d;
dirLight.shadow.camera.top = 60;
dirLight.shadow.camera.bottom = -d;
dirLight.shadow.camera.far = 250;
dirLight.shadow.bias = -0.0001;

// Create a new Object3D for the target of Main DirectionalLight
const targetPosition = new THREE.Object3D();
targetPosition.position.set(0, -15, 0);
scene.add(targetPosition);
dirLight.target = targetPosition;

/**
 * Material
 */

const holoMaterial = new THREE.ShaderMaterial({
	vertexShader: holographicVertexShader,
	fragmentShader: holographicFragmentShader,
	uniforms: {
		uTime: new THREE.Uniform(0),
		uColor: new THREE.Uniform(new THREE.Color(debugObject.holoColor)),
	},
	// transparent: true,
	side: THREE.DoubleSide,
	depthWrite: false,
	blending: THREE.AdditiveBlending,
});

const glowMaterial = new THREE.ShaderMaterial({
	vertexShader: holographicVertexShader,
	fragmentShader: holographicFragmentShader,
	uniforms: {
		uTime: new THREE.Uniform(0),
		uColor: new THREE.Uniform(new THREE.Color(debugObject.glowColor)),
	},
	// transparent: true,
	side: THREE.DoubleSide,
	depthWrite: false,
	blending: THREE.AdditiveBlending,
});

const gridGlowMaterial = new THREE.ShaderMaterial({
	vertexShader: gridGlowVertexShader,
	fragmentShader: gridGlowFragmentShader,
	uniforms: {
		uTime: { value: 0.0 },
		uResolution: { value: new THREE.Vector2(150, 150) },
		uAlpha: { value: 0.5 }, // Transparency
		uLineColor: { value: new THREE.Color("#c7ba9e") }, // Grid line color
		glowStrength: { value: 0.02 }, // Initial glow strength
	},
	transparent: true,
});

/**
 * Floor
 */
const mainfloor = new THREE.Mesh(
	new THREE.PlaneGeometry(1000, 1000),
	threeToon01,
);
mainfloor.receiveShadow = true;
mainfloor.position.y = -0.3; // Slightly raise the grid to avoid z-fighting
mainfloor.rotation.x = -Math.PI * 0.5;
scene.add(mainfloor);

// Re-create the floor mesh with the new shader material
const floorGeometry = new THREE.PlaneGeometry(150, 150);
const floorMesh = new THREE.Mesh(floorGeometry, gridGlowMaterial);
floorMesh.rotation.x = -Math.PI * 0.5;
floorMesh.position.y = -0.1; // Slightly raise the grid to avoid z-fighting
floorMesh.renderOrder = -1; // Ensure grid is rendered first
scene.add(floorMesh);

// /**
//  * Scene Models
//  */

// Wheels
// Load small wheel models for scene models
function loadAndSetupWheelModel(filePath, posX, posY, posZ, material) {
	gltfLoader.load(filePath, (gltf) => {
		const sceneModel = gltf.scene;
		const box = new THREE.Box3().setFromObject(sceneModel);
		const center = box.getCenter(new THREE.Vector3());

		sceneModel.traverse((child) => {
			if (child.isMesh) {
				child.geometry.translate(-center.x, -center.y, -center.z);
				child.material = material; // Use the specified material
			}
		});

		const wheelObject = new THREE.Object3D();
		wheelObject.rotation.set(0, -1.57, 1.57);
		wheelObject.position.set(posX, posY, posZ);
		wheelObject.add(sceneModel);
		sceneModel.scale.set(20, 20, 0);
		sceneModel.rotation.y = -1.57;
		scene.add(wheelObject);
		wheelModels.push(sceneModel);
	});
}

loadAndSetupWheelModel("./flat-wheel.glb", 0, -0.2, 0, threeToon01);
loadAndSetupWheelModel("./flat-wheel.glb", -0.5, -0.2, -72, threeToon01);
loadAndSetupWheelModel("./flat-wheel.glb", 53, -0.2, -30, threeToon01);
loadAndSetupWheelModel("./flat-wheel.glb", 62, -0.2, 26.5, threeToon01);
loadAndSetupWheelModel("./flat-wheel.glb", 22, -0.2, 58, threeToon01);
loadAndSetupWheelModel("./flat-wheel.glb", -30, -0.1, 68, threeToon08);
loadAndSetupWheelModel("./flat-wheel.glb", -55, -0.2, 22, threeToon01);
loadAndSetupWheelModel("./flat-wheel.glb", -57, -0.2, -33, threeToon01);

// Load large wheel models for scene sky intro
gltfLoader.load("./wheel.glb", (gltf) => {
	const sceneModel = gltf.scene;

	const box = new THREE.Box3().setFromObject(sceneModel);
	const center = box.getCenter(new THREE.Vector3());

	sceneModel.traverse((child) => {
		if (child.isMesh) {
			// Move each mesh to center the geometry
			child.geometry.translate(-center.x, -center.y, -center.z);
			child.material = threeToon12;
		}
	});

	// Create a parent object for initial rotation
	const parentObject = new THREE.Object3D();
	scene.add(parentObject);

	// Set initial rotation to the parent object
	parentObject.rotation.set(0, -1.55, 1.55); // Adjust these values as needed'
	parentObject.position.set(0, 45, 0);

	// Add the model as a child of the parent object
	parentObject.add(sceneModel);

	// Set initial position and scale for the model
	sceneModel.scale.set(35, 35, 35);
	sceneModel.rotation.x = 30;

	// Store the child for animation
	sceneWheelModel01 = sceneModel; // This now refers to the child object
});
gltfLoader.load("./wheel.glb", (gltf) => {
	const sceneModel = gltf.scene;

	const box = new THREE.Box3().setFromObject(sceneModel);
	const center = box.getCenter(new THREE.Vector3());

	sceneModel.traverse((child) => {
		if (child.isMesh) {
			// Move each mesh to center the geometry
			child.geometry.translate(-center.x, -center.y, -center.z);
			child.material = threeToon12;
		}
	});

	// Create a parent object for initial rotation
	const parentObject = new THREE.Object3D();
	scene.add(parentObject);

	// Set initial rotation to the parent object
	parentObject.rotation.set(0, -1.55, 1.55); // Adjust these values as needed'
	parentObject.position.set(0, 70, 0);

	// Add the model as a child of the parent object
	parentObject.add(sceneModel);

	// Set initial position and scale for the model
	sceneModel.scale.set(35, 35, 35);
	sceneModel.rotation.x = 60;

	// Store the child for animation
	sceneWheelModel02 = sceneModel; // This now refers to the child object
});
gltfLoader.load("./wheel.glb", (gltf) => {
	const sceneModel = gltf.scene;

	const box = new THREE.Box3().setFromObject(sceneModel);
	const center = box.getCenter(new THREE.Vector3());

	sceneModel.traverse((child) => {
		if (child.isMesh) {
			// Move each mesh to center the geometry
			child.geometry.translate(-center.x, -center.y, -center.z);
			child.material = threeToon12;
		}
	});

	// Create a parent object for initial rotation
	const parentObject = new THREE.Object3D();
	scene.add(parentObject);

	// Set initial rotation to the parent object
	parentObject.rotation.set(0, -1.55, 1.55); // Adjust these values as needed'
	parentObject.position.set(0, 90, 0);

	// Add the model as a child of the parent object
	parentObject.add(sceneModel);

	// Set initial position and scale for the model
	sceneModel.scale.set(35, 35, 35);
	sceneModel.rotation.x = 90;

	// Store the child for animation
	sceneWheelModel03 = sceneModel; // This now refers to the child object
});

// Load trees model
function Mulberry32(a) {
	return function () {
		var t = (a += 0x6d2b79f5);
		t = Math.imul(t ^ (t >>> 15), t | 1);
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

let generalSeed = 746412512; // Fixed seed for consistency in general models
let wheelSeed = 124878543; // Different fixed seed for wheel models to maintain isolated consistency

const masterRandom = Mulberry32(generalSeed);
const wheelRandom = Mulberry32(wheelSeed); // Dedicated random function for wheels

function createBakedRandom(count) {
	const values = [];
	for (let i = 0; i < count; i++) {
		values.push(masterRandom());
	}
	let index = 0;
	return () => values[index++];
}

const decorativeRandoms = {
	TREES_OUTER: createBakedRandom(30 * 5),
	TREES_INNER: createBakedRandom(30 * 5),
	STONES_INNER: createBakedRandom(20 * 6),
	STONES_OUTER: createBakedRandom(15 * 6),
	LEAVES: createBakedRandom(15 * 6),
};

let sceneModels = new THREE.Group();
scene.add(sceneModels);

gltfLoader.load("./tree.glb", (gltf) => {
	const random = decorativeRandoms.TREES_OUTER;
	const originalScene = gltf.scene;
	sceneModels = new THREE.Group();
	sceneModels.position.set(0, 0, 0);
	sceneModels.rotation.y = -0.1;
	scene.add(sceneModels);

	const materials = [threeToon03, threeToon04, threeToon05];

	for (let i = 0; i < 30; i++) {
		const angle = random() * Math.PI * -1; // Random angle using seedable RNG
		const radius = 75 + random() * 16; // Random radius using seedable RNG
		const x = Math.cos(angle) * radius;
		const z = Math.sin(angle) * radius;

		const sceneModel = originalScene.clone();
		sceneModel.scale.set(4, 6, 4);
		sceneModel.position.set(x, -0.1, z);

		sceneModel.rotation.z = (random() - 0.5) * 0.4;
		sceneModel.rotation.y = (random() - 0.5) * 0.4;

		sceneModel.traverse((child) => {
			if (child.isMesh) {
				child.castShadow = true;
				child.receiveShadow = true;

				if (child.name === "ob1") {
					child.material = materials[Math.floor(random() * materials.length)];
				}
				if (child.name === "ob2") {
					child.material = threeToon07;
				}
			}
		});

		sceneModels.add(sceneModel);
	}
});
gltfLoader.load("./tree.glb", (gltf) => {
	const random = decorativeRandoms.TREES_INNER;
	const originalScene = gltf.scene;
	sceneModels = new THREE.Group();
	sceneModels.position.set(0, 0, 0);
	sceneModels.rotation.y = -0.1;
	scene.add(sceneModels);

	const materials = [threeToon03, threeToon04, threeToon05];

	for (let i = 0; i < 30; i++) {
		const angle = random() * Math.PI * 2; // Random angle using seedable RNG
		const radius = 12 + random() * 16; // Random radius using seedable RNG
		const x = Math.cos(angle) * radius;
		const z = Math.sin(angle) * radius;

		const sceneModel = originalScene.clone();
		sceneModel.scale.set(4, 6, 4);
		sceneModel.position.set(x, -0.1, z);

		sceneModel.rotation.z = (random() - 0.5) * 0.4;
		sceneModel.rotation.y = (random() - 0.5) * 0.4;

		sceneModel.traverse((child) => {
			if (child.isMesh) {
				child.castShadow = true;
				child.receiveShadow = true;

				if (child.name === "ob1") {
					child.material = materials[Math.floor(random() * materials.length)];
				}
				if (child.name === "ob2") {
					child.material = threeToon07;
				}
			}
		});

		sceneModels.add(sceneModel);
	}
});
gltfLoader.load("./stone.glb", (gltf) => {
	const random = decorativeRandoms.STONES_INNER;
	const originalScene = gltf.scene;
	sceneModels = new THREE.Group();
	sceneModels.position.set(0, -0.1, 0);
	sceneModels.rotation.y = -0.3; // Initial rotation of the group
	scene.add(sceneModels);

	const materials = [threeToon11, threeToon12];

	for (let i = 0; i < 20; i++) {
		const angle = random() * Math.PI * 2; // Random angle using seedable RNG
		const radius = 12 + random() * 12; // Random radius using seedable RNG
		const x = Math.cos(angle) * radius;
		const z = Math.sin(angle) * radius;

		const sceneModel = originalScene.clone();
		sceneModel.position.set(x, -0.1, z);

		// Random rotation
		sceneModel.rotation.z = (random() - 0.5) * 0.2;
		sceneModel.rotation.y = (random() - 0.5) * -0.3;

		// Random scale, avoid squishing too much
		const scale = 0.5 + random() * 2.5; // Ensures scaling is never too small and not overly large
		sceneModel.scale.set(scale, scale, scale);

		sceneModel.traverse((child) => {
			if (child.isMesh) {
				child.castShadow = true;
				child.receiveShadow = true;

				// Apply random materials
				if (child.name === "ob1") {
					child.material = materials[Math.floor(random() * materials.length)];
				}
			}
		});

		sceneModels.add(sceneModel);
	}
});
gltfLoader.load("./stone.glb", (gltf) => {
	const random = decorativeRandoms.STONES_OUTER;
	const originalScene = gltf.scene;
	sceneModels = new THREE.Group();
	sceneModels.position.set(0, -0.1, 0);
	sceneModels.rotation.y = -0.3; // Initial rotation of the group
	scene.add(sceneModels);

	const materials = [threeToon11, threeToon12];

	for (let i = 0; i < 15; i++) {
		const angle = random() * Math.PI * 2; // Random angle using seedable RNG
		const radius = 60 + random() * 12; // Random radius using seedable RNG
		const x = Math.cos(angle) * radius;
		const z = Math.sin(angle) * radius;

		const sceneModel = originalScene.clone();
		sceneModel.position.set(x, -0.1, z);

		// Random rotation
		sceneModel.rotation.z = (random() - 0.5) * 0.2;
		sceneModel.rotation.y = (random() - 0.5) * -0.3;

		// Random scale, avoid squishing too much
		const scale = 0.5 + random() * 1.5; // Ensures scaling is never too small and not overly large
		sceneModel.scale.set(scale, scale, scale);

		sceneModel.traverse((child) => {
			if (child.isMesh) {
				child.castShadow = true;
				child.receiveShadow = true;

				// Apply random materials
				if (child.name === "ob1") {
					child.material = materials[Math.floor(random() * materials.length)];
				}
			}
		});

		sceneModels.add(sceneModel);
	}
});
gltfLoader.load("./leaf.glb", (gltf) => {
	const random = decorativeRandoms.LEAVES;
	const originalScene = gltf.scene;
	sceneModels = new THREE.Group();
	sceneModels.position.set(-30, -0.2, 68);
	sceneModels.rotation.y = -0.3; // Initial rotation of the group
	scene.add(sceneModels);

	const materials = [threeToon07, threeToon08];

	for (let i = 0; i < 15; i++) {
		const angle = random() * Math.PI * 2; // Random angle using seedable RNG
		const radius = 10 + random() * 12; // Random radius using seedable RNG
		const x = Math.cos(angle) * radius;
		const z = Math.sin(angle) * radius;

		const sceneModel = originalScene.clone();
		sceneModel.position.set(x, -0.1, z);

		// Random rotation
		sceneModel.rotation.z = (random() - 0.5) * 0.2;
		sceneModel.rotation.y = (random() - 0.5) * -0.3;

		// Random scale, avoid squishing too much
		const scale = 0.5 + random() * 1.5; // Ensures scaling is never too small and not overly large
		sceneModel.scale.set(scale, scale, scale);

		sceneModel.traverse((child) => {
			if (child.isMesh) {
				child.castShadow = true;
				child.receiveShadow = true;

				// Apply random materials
				if (child.name === "ob1") {
					child.material = materials[Math.floor(random() * materials.length)];
				}
			}
		});

		sceneModels.add(sceneModel);
	}
});
// Load wheel model
gltfLoader.load("./wheel.glb", (gltf) => {
	const localWheelRandom = wheelRandom;
	const wheelModels = new THREE.Group(); // Use a specific group for wheels
	wheelModels.position.set(-62.5, 0, 25);
	wheelModels.rotation.y = -0.3;
	scene.add(wheelModels);

	const materials = [threeToon04, threeToon08];

	for (let i = 0; i < 10; i++) {
		// Use wheelRandom for wheel-specific calculations
		const angle = localWheelRandom() * Math.PI * 2;
		const radius = 6 + localWheelRandom() * 12;

		const x = Math.cos(angle) * radius;
		const z = Math.sin(angle) * radius;

		const sceneModel = gltf.scene.clone();
		sceneModel.position.set(x, -0.1, z);

		const scale = 0.5 + localWheelRandom() * 1.5;
		sceneModel.scale.set(scale, scale, scale);

		sceneModel.rotation.z = (localWheelRandom() - 0.5) * 0.2;
		sceneModel.rotation.y = (localWheelRandom() - 0.5) * -0.3;

		sceneModel.rotation.set(0, -1.55, 1.55); // Adjust these values as needed'

		sceneModel.traverse((child) => {
			if (child.isMesh) {
				child.material =
					materials[Math.floor(localWheelRandom() * materials.length)];
			}
		});

		wheelModels.add(sceneModel);
	}
});

// Scene Model
gltfLoader.load("./scene.glb", (gltf) => {
	gltf.scene.position.set(0, -0.2, 0);

	// Add GLTF Object
	const sceneModel = gltf.scene;
	scene.add(sceneModel);

	// Animation
	sceneMixer = new THREE.AnimationMixer(sceneModel);
	const action = sceneMixer.clipAction(gltf.animations[0]);
	action.play(); // Start the animation

	// Get each object and apply material
	const ob1 = gltf.scene.children.find((child) => child.name === "ob1");
	const ob4 = gltf.scene.children.find((child) => child.name === "ob4");

	// Pyramid
	const ob7 = gltf.scene.children.find((child) => child.name === "ob7");
	const ob7_1 = ob7.children.find((child) => child.name === "ob7_1");
	const ob7_2 = ob7.children.find((child) => child.name === "ob7_2");
	const ob7_3 = ob7.children.find((child) => child.name === "ob7_3");
	const ob7_4 = ob7.children.find((child) => child.name === "ob7_4");
	const ob7_5 = ob7.children.find((child) => child.name === "ob7_5");
	const ob7_6 = ob7.children.find((child) => child.name === "ob7_6");
	const ob7_7 = ob7.children.find((child) => child.name === "ob7_7");

	const ob11 = gltf.scene.children.find((child) => child.name === "ob11");
	const ob11_1 = gltf.scene.children.find((child) => child.name === "ob11_1");
	const ob13 = gltf.scene.children.find((child) => child.name === "ob13");
	const ob14 = gltf.scene.children.find((child) => child.name === "ob14");

	const ob15 = gltf.scene.children.find((child) => child.name === "ob15");
	const ob15_1 = ob15.children.find((child) => child.name === "ob15_1");
	const ob15_2 = ob15.children.find((child) => child.name === "ob15_2");
	const ob15_3 = ob15.children.find((child) => child.name === "ob15_3");
	const ob15_4 = ob15.children.find((child) => child.name === "ob15_4");
	const ob15_5 = ob15.children.find((child) => child.name === "ob15_5");

	const ob16 = gltf.scene.children.find((child) => child.name === "ob16");
	const ob17 = gltf.scene.children.find((child) => child.name === "ob17");
	const ob18 = gltf.scene.children.find((child) => child.name === "ob18");
	const ob19 = gltf.scene.children.find((child) => child.name === "ob19");

	const ob20 = gltf.scene.children.find((child) => child.name === "ob20");

	// obj
	ob1.material = threeToon12;

	// Femce
	ob4.material = threeToon04;

	// Pyramid
	ob7_1.material = threeToon01;
	ob7_2.material = threeToon11;
	ob7_3.material = threeToon01;
	ob7_4.material = threeToon11;
	ob7_5.material = threeToon01;
	ob7_6.material = threeToon11;
	ob7_7.material = threeToon01;

	// Aljazri House
	ob11.material = threeToon12;
	ob11_1.material = threeToon08;

	// Stage
	ob13.material = threeToon07;

	// Building 01
	ob14.material = threeToon11;

	// Book Table
	ob15_1.material = threeToon10;
	ob15_2.material = threeToon12;
	ob15_3.material = threeToon08;
	ob15_4.material = threeToon11;
	ob15_5.material = threeToon10;

	// Crank
	ob16.material = glowMaterial;
	ob17.material = glowMaterial;
	ob18.material = glowMaterial;
	ob19.material = glowMaterial;

	// Extra
	ob20.material = threeToon11;

	// Enable shadow casting for each mesh in the model
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.castShadow = true;
			child.receiveShadow = true;
		}
	});
});

// Flag Model
gltfLoader.load("./flag.glb", (gltf) => {
	gltf.scene.position.set(33.5, 4, -72);

	// Add GLTF Object
	const flagModel = gltf.scene;
	scene.add(flagModel);

	// Animation
	flagMixer = new THREE.AnimationMixer(flagModel);
	const flagAction = flagMixer.clipAction(gltf.animations[0]);
	flagAction.play(); // Start the animation

	/**
	 * Shadow and Material
	 */

	// Material
	const ob1 = gltf.scene.children.find((child) => child.name === "ob1");
	const ob2 = gltf.scene.children.find((child) => child.name === "ob2");
	const ob3 = gltf.scene.children.find((child) => child.name === "ob3");

	ob1.material = flag01Material;
	ob2.material = threeToon04;
	ob3.material = threeToon12;

	// Enable shadow casting for each mesh in the model
	flagModel.traverse((child) => {
		if (child.isMesh) {
			child.castShadow = true;
			child.receiveShadow = true;
		}
	});
});

// /**
//  * Automata Models
//  */

// Model 01
gltfLoader.load("./main.glb", (gltf) => {
	gltf.scene.position.set(0, -0.1, 0);
	gltf.scene.scale.set(0.85, 0.85, 0.85);
	gltf.scene.rotation.set(0, 0.25, 0);

	// Add GLTF Object
	const sceneModel = gltf.scene;
	scene.add(sceneModel);

	// Animation

	// Correct approach to pause animations upon loading
	const mixer = new THREE.AnimationMixer(sceneModel);
	mixers[MODEL_INDEX.MAIN] = mixer;

	const action = mixer.clipAction(gltf.animations[0]);
	action.play(); // Start playing the animation
	action.paused = true; // Immediately pause the animation

	/**
	 * Shadow and Material
	 */

	// Enable shadow casting for each mesh in the model
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.castShadow = true;
			child.receiveShadow = true;
			child.material = threeToon06;
		}
	});

	// Material
	const ob1 = gltf.scene.children.find((child) => child.name === "ob1");
	const ob2 = gltf.scene.children.find((child) => child.name === "ob2");
	const ob3 = gltf.scene.children.find((child) => child.name === "ob3");

	const ob3_1 = ob3.children.find((child) => child.name === "ob3_1");
	const ob3_2 = ob3.children.find((child) => child.name === "ob3_2");
	const ob3_3 = ob3.children.find((child) => child.name === "ob3_3");
	const ob3_4 = ob3.children.find((child) => child.name === "ob3_4");
	const ob3_5 = ob3.children.find((child) => child.name === "ob3_5");

	// ob1.material = threeToon01
	ob2.material = threeToon07;
	ob3.material = threeToon04;

	ob3_1.material = glowMaterial;
	ob3_2.material = glowMaterial;
	ob3_3.material = glowMaterial;
	ob3_4.material = glowMaterial;
	ob3_5.material = holoMaterial;

	// Mark the root object for easy raycasting
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.userData.mixerIndex = MODEL_INDEX.MAIN; // Use mixer's array index for identification
		}
	});
});

// Model 02
gltfLoader.load("./bird.glb", (gltf) => {
	gltf.scene.scale.set(0.35, 0.35, 0.35);
	gltf.scene.rotation.set(0, 1.15 * Math.PI, 0);
	gltf.scene.position.set(-0.5, 4, -72);

	// Add GLTF Object
	const sceneModel = gltf.scene;
	scene.add(sceneModel);

	// Animation

	// Correct approach to pause animations upon loading
	const mixer = new THREE.AnimationMixer(sceneModel);
	mixers[MODEL_INDEX.BIRD] = mixer;

	const action = mixer.clipAction(gltf.animations[0]);
	action.play(); // Start playing the animation
	action.paused = true; // Immediately pause the animation

	/**
	 * Shadow and Material
	 */

	// Enable shadow casting for each mesh in the model
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.castShadow = true;
			child.receiveShadow = true;
		}
	});

	// Material
	const ob1 = gltf.scene.children.find((child) => child.name === "ob1");

	const ob2 = gltf.scene.children.find((child) => child.name === "ob2");
	const ob2_1 = ob2.children.find((child) => child.name === "ob2_1");
	const ob2_2 = ob2.children.find((child) => child.name === "ob2_2");
	const ob2_3 = ob2.children.find((child) => child.name === "ob2_3");
	const ob2_4 = ob2.children.find((child) => child.name === "ob2_4");
	const ob2_5 = ob2.children.find((child) => child.name === "ob2_5");
	const ob2_6 = ob2.children.find((child) => child.name === "ob2_6");
	const ob2_7 = ob2.children.find((child) => child.name === "ob2_7");

	const ob3 = gltf.scene.children.find((child) => child.name === "ob3");

	const ob4 = gltf.scene.children.find((child) => child.name === "ob4");
	const ob4_1 = ob4.children.find((child) => child.name === "ob4_1");

	const ob5 = ob4_1.children.find((child) => child.name === "ob5");
	const ob5_1 = ob5.children.find((child) => child.name === "ob5_1");
	const ob5_2 = ob5.children.find((child) => child.name === "ob5_2");
	const ob5_3 = ob5.children.find((child) => child.name === "ob5_3");
	const ob5_4 = ob5.children.find((child) => child.name === "ob5_4");
	const ob5_5 = ob5.children.find((child) => child.name === "ob5_5");

	const ob6 = gltf.scene.children.find((child) => child.name === "ob6");
	const ob7 = gltf.scene.children.find((child) => child.name === "ob7");
	const ob8 = gltf.scene.children.find((child) => child.name === "ob8");
	const ob9 = gltf.scene.children.find((child) => child.name === "ob9");
	const ob10 = gltf.scene.children.find((child) => child.name === "ob10");
	const ob11 = gltf.scene.children.find((child) => child.name === "ob11");

	ob1.material = threeToon12;

	ob2_1.material = threeToon01;
	ob2_2.material = threeToon06;
	ob2_3.material = threeToon06;
	ob2_4.material = threeToon06;
	ob2_5.material = threeToon06;
	ob2_6.material = threeToon06;
	ob2_7.material = threeToon01;

	ob3.material = threeToon07;

	ob4_1.material = threeToon01;

	ob5_1.material = threeToon08;
	ob5_2.material = threeToon04;
	ob5_3.material = threeToon04;
	ob5_4.material = threeToon04;
	ob5_5.material = threeToon04;

	ob6.material = threeToon07;
	ob7.material = threeToon07;
	ob8.material = threeToon11;
	ob9.material = threeToon08;
	ob10.material = threeToon11;
	ob11.material = threeToon11;

	// Mark the root object for easy raycasting
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.userData.mixerIndex = MODEL_INDEX.BIRD; // Use mixer's array index for identification
		}
	});
});

// Model 03
gltfLoader.load("./caren-comp.glb", (gltf) => {
	gltf.scene.position.set(46.5, 2.5, -42);
	gltf.scene.rotation.set(0, 0.5, 0);

	// Add GLTF Object
	const sceneModel = gltf.scene;
	scene.add(sceneModel);

	// Animation

	// Correct approach to pause animations upon loading
	const mixer = new THREE.AnimationMixer(sceneModel);
	mixers[MODEL_INDEX.CAREN] = mixer;

	const action = mixer.clipAction(gltf.animations[0]);
	action.play(); // Start playing the animation
	action.paused = true; // Immediately pause the animation

	/**
	 * Shadow and Material
	 */

	// Enable shadow casting for each mesh in the model
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.castShadow = true;
			child.receiveShadow = true;
			child.material = threeToon01;
		}
	});

	// Material
	const ob0 = gltf.scene.children.find((child) => child.name === "ob0");
	const ob1 = ob0.children.find((child) => child.name === "ob1");
	const ob2 = ob0.children.find((child) => child.name === "ob2");
	const ob3 = ob0.children.find((child) => child.name === "ob3");
	const ob4 = ob0.children.find((child) => child.name === "ob4");
	const ob5 = ob0.children.find((child) => child.name === "ob5");
	const ob6 = ob0.children.find((child) => child.name === "ob6");
	const ob7 = ob0.children.find((child) => child.name === "ob7");
	const ob8 = ob0.children.find((child) => child.name === "ob8");
	const ob9 = ob0.children.find((child) => child.name === "ob9");

	ob1.material = threeToon10;
	ob2.material = threeToon12;
	ob3.material = threeToon07;
	ob4.material = threeToon10;
	ob5.material = threeToon10;
	ob6.material = threeToon11;
	ob7.material = threeToon07;
	ob8.material = threeToon11;
	ob9.material = threeToon10;

	// Mark the root object for easy raycasting
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.userData.mixerIndex = MODEL_INDEX.CAREN; // Use mixer's array index for identification
		}
	});
});

// Model 04
gltfLoader.load("./crank.glb", (gltf) => {
	gltf.scene.position.set(61.75, 0.7, 26.25);
	gltf.scene.scale.set(0.85, 0.85, 0.85);
	gltf.scene.rotation.set(0, 0.45, 0);

	// Add GLTF Object
	const sceneModel = gltf.scene;
	scene.add(sceneModel);

	// Animation

	// Correct approach to pause animations upon loading
	const mixer = new THREE.AnimationMixer(sceneModel);
	mixers[MODEL_INDEX.CRANK] = mixer;

	const action = mixer.clipAction(gltf.animations[0]);
	action.play(); // Start playing the animation
	action.paused = true; // Immediately pause the animation

	/**
	 * Shadow and Material
	 */

	// Enable shadow casting for each mesh in the model
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.castShadow = true;
			child.receiveShadow = true;
		}
	});

	// Material
	const ob1 = gltf.scene.children.find((child) => child.name === "ob1");
	const ob2 = gltf.scene.children.find((child) => child.name === "ob2");
	const ob3 = gltf.scene.children.find((child) => child.name === "ob3");

	ob1.material = holoMaterial;
	ob2.material = holoMaterial;
	ob3.material = threeToon12;

	// Mark the root object for easy raycasting
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.userData.mixerIndex = MODEL_INDEX.CRANK; // Use mixer's array index for identification
		}
	});
});

// Model 05
gltfLoader.load("./marble.glb", (gltf) => {
	gltf.scene.position.set(21, 4, 57);
	gltf.scene.rotation.set(0, 0.6 * Math.PI, 0);

	// Add GLTF Object
	const sceneModel = gltf.scene;
	scene.add(sceneModel);

	// Animation

	// Correct approach to pause animations upon loading
	const mixer = new THREE.AnimationMixer(sceneModel);
	mixers[MODEL_INDEX.MARBLE] = mixer;

	const action = mixer.clipAction(gltf.animations[0]);
	action.play(); // Start playing the animation
	action.paused = true; // Immediately pause the animation

	/**
	 * Shadow and Material
	 */
	// Enable shadow casting for each mesh in the model
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.castShadow = true;
			child.receiveShadow = true;
		}
	});

	// Material
	const ob1 = gltf.scene.children.find((child) => child.name === "ob1");
	const ob1_1 = ob1.children.find((child) => child.name === "ob1_1");
	const ob1_2 = ob1.children.find((child) => child.name === "ob1_2");
	const ob1_3 = ob1.children.find((child) => child.name === "ob1_3");
	const ob1_4 = ob1.children.find((child) => child.name === "ob1_4");
	const ob1_5 = ob1.children.find((child) => child.name === "ob1_5");

	const ob2 = gltf.scene.children.find((child) => child.name === "ob2");
	const ob2_1 = ob2.children.find((child) => child.name === "ob2_1");
	const ob2_2 = ob2.children.find((child) => child.name === "ob2_2");
	const ob2_3 = ob2.children.find((child) => child.name === "ob2_3");
	const ob2_4 = ob2.children.find((child) => child.name === "ob2_4");
	const ob2_5 = ob2.children.find((child) => child.name === "ob2_5");
	const ob2_6 = ob2.children.find((child) => child.name === "ob2_6");
	const ob2_7 = ob2.children.find((child) => child.name === "ob2_7");
	const ob2_8 = ob2.children.find((child) => child.name === "ob2_8");

	const ob3 = gltf.scene.children.find((child) => child.name === "ob3");
	const ob4 = gltf.scene.children.find((child) => child.name === "ob4");
	const ob5 = gltf.scene.children.find((child) => child.name === "ob5");
	const ob6 = gltf.scene.children.find((child) => child.name === "ob6");

	const ob7 = gltf.scene.children.find((child) => child.name === "ob7");
	const ob7_1 = ob7.children.find((child) => child.name === "ob7_1");
	const ob7_2 = ob7.children.find((child) => child.name === "ob7_2");
	const ob7_3 = ob7.children.find((child) => child.name === "ob7_3");
	const ob7_4 = ob7.children.find((child) => child.name === "ob7_4");
	const ob7_5 = ob7.children.find((child) => child.name === "ob7_5");

	ob1.material = threeToon09;
	ob1_1.material = threeToon12;
	ob1_2.material = threeToon12;
	ob1_3.material = threeToon12;
	ob1_4.material = threeToon12;
	ob1_5.material = threeToon12;

	ob2_1.material = threeToon06;
	ob2_2.material = threeToon06;
	ob2_3.material = threeToon06;
	ob2_4.material = threeToon06;
	ob2_5.material = threeToon06;
	ob2_6.material = threeToon04;
	ob2_7.material = threeToon04;
	ob2_8.material = threeToon06;

	ob3.material = threeToon03;
	ob4.material = threeToon04;
	ob5.material = threeToon05;
	ob6.material = threeToon09;

	ob7_1.material = threeToon11;
	ob7_2.material = threeToon03;
	ob7_3.material = threeToon10;
	ob7_4.material = threeToon03;
	ob7_5.material = threeToon10;

	// Mark the root object for easy raycasting
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.userData.mixerIndex = MODEL_INDEX.MARBLE; // Use mixer's array index for identification
		}
	});
});

// Model 06
gltfLoader.load("./noria.glb", (gltf) => {
	gltf.scene.position.set(-30, -0.2, 68);
	gltf.scene.scale.set(15, 15, 15);
	gltf.scene.rotation.set(0, 5, 0);

	// Add GLTF Object
	const sceneModel = gltf.scene;
	scene.add(sceneModel);

	// Animation

	// Correct approach to pause animations upon loading
	const mixer = new THREE.AnimationMixer(sceneModel);
	mixers[MODEL_INDEX.NORIA] = mixer;

	const action = mixer.clipAction(gltf.animations[0]);
	action.play(); // Start playing the animation
	action.paused = true; // Immediately pause the animation

	/**
	 * Shadow and Material
	 */

	// Enable shadow casting for each mesh in the model
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.castShadow = true;
			child.receiveShadow = true;
			child.material = threeToon01;
		}
	});

	// Material
	const ob1 = gltf.scene.children.find((child) => child.name === "ob1");
	const ob2 = gltf.scene.children.find((child) => child.name === "ob2");
	const ob3 = gltf.scene.children.find((child) => child.name === "ob3");
	const ob4 = gltf.scene.children.find((child) => child.name === "ob4");

	ob1.material = threeToon04;
	ob2.material = threeToon07;
	ob3.material = threeToon08Water;
	ob4.material = threeToon09;

	// Mark the root object for easy raycasting
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.userData.mixerIndex = MODEL_INDEX.NORIA; // Use mixer's array index for identification
		}
	});
});

// Model 07
gltfLoader.load("./aljazri.glb", (gltf) => {
	gltf.scene.position.set(-53, 0, 24.5);
	gltf.scene.scale.set(3.5, 3.5, 3.5);
	gltf.scene.rotation.set(0, 1.9 * Math.PI, 0);

	// Add GLTF Object
	const sceneModel = gltf.scene;
	scene.add(sceneModel);

	// Animation

	// Correct approach to pause animations upon loading
	const mixer = new THREE.AnimationMixer(sceneModel);
	mixers[MODEL_INDEX.ALJAZARI] = mixer;

	const action = mixer.clipAction(gltf.animations[0]);
	action.play(); // Start playing the animation
	action.paused = true; // Immediately pause the animation

	/**
	 * Shadow and Material
	 */

	// Enable shadow casting for each mesh in the model
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.castShadow = true;
			child.receiveShadow = true;
		}
	});

	// Material
	const ob2 = gltf.scene.children.find((child) => child.name === "ob2");
	const ob3 = gltf.scene.children.find((child) => child.name === "ob3");

	ob2.material = threeToon05;
	ob3.material = threeToon10;

	// Mark the root object for easy raycasting
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.userData.mixerIndex = MODEL_INDEX.ALJAZARI; // Use mixer's array index for identification
		}
	});
});

// Model 08
gltfLoader.load("./solar.glb", (gltf) => {
	gltf.scene.position.set(-57, 0, -33);
	gltf.scene.scale.set(1.5, 1.5, 1.5);
	gltf.scene.rotation.set(0, 4, 0);

	// Add GLTF Object
	const sceneModel = gltf.scene;
	scene.add(sceneModel);

	// Animation

	// Correct approach to pause animations upon loading
	const mixer = new THREE.AnimationMixer(sceneModel);
	mixers[MODEL_INDEX.SOLAR] = mixer;

	const action = mixer.clipAction(gltf.animations[0]);
	action.play(); // Start playing the animation
	action.paused = true; // Immediately pause the animation

	/**
	 * Shadow and Material
	 */

	// Enable shadow casting for each mesh in the model
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.castShadow = true;
			child.receiveShadow = true;
		}
	});

	// Material
	const ob1 = gltf.scene.children.find((child) => child.name === "ob1");
	const ob1_1 = ob1.children.find((child) => child.name === "ob1_1");
	const ob2 = gltf.scene.children.find((child) => child.name === "ob2");
	const ob3 = gltf.scene.children.find((child) => child.name === "ob3");
	const ob4 = gltf.scene.children.find((child) => child.name === "ob4");
	const ob5 = gltf.scene.children.find((child) => child.name === "ob5");
	const ob6 = gltf.scene.children.find((child) => child.name === "ob6");

	ob1.material = threeToon11;
	ob1_1.material = threeToon09;
	ob2.material = threeToon06;
	ob3.material = threeToon06;
	ob4.material = holoMaterial;
	ob5.material = threeToon02;
	ob6.material = threeToon04;

	// Mark the root object for easy raycasting
	sceneModel.traverse((child) => {
		if (child.isMesh) {
			child.userData.mixerIndex = MODEL_INDEX.SOLAR; // Use mixer's array index for identification
		}
	});
});

/**
 * Raycaster and EventListener
 */
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

// Function to show model information and buttons
function showModelUI(index) {
	const modelInfoContent = [
		{
			name: "Treasure Chest of Knowledge",
			description:
				"Welcome to 'Automata Experience: From Simplicity to Complexity.' This journey begins with the unlocking of the Treasure Chest, an entry point into the fascinating evolution of mechanical devices. Each piece within represents a significant leap in the understanding and application of automata, from ancient innovations to contemporary advancements. Engage with this experience to explore how simple machines laid the groundwork for complex automated systems and discover the profound impact of automata on technology and culture throughout history.",
		},
		{
			name: "DIY Automata Toy",
			description:
				"This charming DIY Toy Box encapsulates the spirit of early mechanical toys. Each piece within this box is a testament to the basic principles of cams and gears used historically in toys and small automata. As you watch these simple wooden figures come to life, understand the foundational mechanics that are the precursors to more complex automated systems, highlighting the transition from manually operated devices to fully autonomous machines.",
		},
		{
			name: "The Crane",
			description:
				"The Crane rises as a symbol of human ingenuity in leveraging simple machines to achieve great feats. This model demonstrates the combined power of levers, pulleys, and wheels, integral to building not just buildings but also the framework for early automata. As the crane lifts its load, visualize the historical landscape where such machines were not just tools of construction but also of inspiration for the first automata engineers.",
		},
		{
			name: "The Crankshaft",
			description:
				"Central to both the functionality of ancient water clocks and modern engines, the Crankshaft transforms rotational motion into a powerful linear force. This animation showcases its mechanism in action, a critical component in the evolution from static sculptures to dynamic automata. The crankshaft's continuous movement mimics the rhythmic motions found in living beings, bridging the gap between static machines and the animated wonders of automata.",
		},
		{
			name: "Marble Stairs Automata",
			description:
				"Observe the Marble Stairs Automata, where marbles gracefully descend inclined planes, simulating a mesmerizing dance orchestrated by gravity and simple mechanical interactions. This exhibit illustrates how automata can transform a simple physical principle into a spectacle, making the abstract concepts of motion and force visually captivating and fundamentally engaging. It's a playful yet profound display of how basic elements underpin complex automated systems.",
		},
		{
			name: "Noria",
			description:
				"The Noria stands as a monumental example of using natural forces in automata. Powered by water, this grand wheel lifts life-giving liquid, showcasing the practical application of rotational mechanics which are crucial in automata designs. As the wheel turns, it embodies the seamless integration of the natural environment with mechanical innovation, a harmony that's essential in the design of ecological automata today.",
		},
		{
			name: "Ismail Al-Jazari:<br> The Father of Robotics",
			description:
				"Step into the world of Ismail Al-Jazari, the brilliant 12th-century inventor known for revolutionizing mechanical engineering. From crafting Norias that irrigated fields, to developing the crankshaft and authoring 'The Book of Knowledge of Ingenious Mechanical Devices,' Al-Jazari's contributions laid the foundation for modern robotics. His ingenuity and creativity continue to inspire, bridging the gap between ancient mechanics and contemporary automation.",
		},
		{
			name: "Solar Automata",
			description:
				"The Solar Automata model brings the future of automation into the present, harnessing solar energy to power a self-sustaining robot. This display exemplifies the potential of integrating renewable energy sources with robotic technology, illustrating a sustainable path forward in automata development. Watch as the solar panels capture light, powering the mechanisms that animate this eco-friendly automaton.",
		},
		// Add entries for each model
	];

	// Update UI content based on the model
	const info = modelInfoContent[index];
	document.getElementById("model-info").innerHTML =
		`<h1>${info.name}</h1><p>${info.description}</p>`;

	// Show UI container with fade-in effect
	gsap.to(".ui-container", { opacity: 1, duration: 0.5, display: "block" });
}

// Function to hide model information and buttons
function hideModelUI() {
	// Hide UI container with fade-out effect
	gsap.to(".ui-container", { opacity: 0, duration: 0.5, display: "none" });
}
// Define a function to handle keyboard controls
function handleKeydown(event) {
	if (controls.keys[event.code]) {
		event.preventDefault();
		return;
	}

	const speed = 5;
	const moveVector = new THREE.Vector3();

	switch (event.code) {
		case controls.keys.LEFT:
		case "KeyA":
			moveVector.x -= speed;
			break;
		case controls.keys.UP:
		case "KeyW":
			moveVector.z -= speed;
			break;
		case controls.keys.RIGHT:
		case "KeyD":
			moveVector.x += speed;
			break;
		case controls.keys.BOTTOM:
		case "KeyS":
			moveVector.z += speed;
			break;
	}

	moveVector.applyQuaternion(camera.quaternion);
	moveVector.setY(0);

	gsap.to([camera.position, controls.target], {
		duration: 0.5,
		x: "+=" + moveVector.x,
		y: "+=" + moveVector.y,
		z: "+=" + moveVector.z,
		ease: "power2.out",
	});
}

// Define functions to manage keyboard listener
function enableKeyboardControls() {
	window.addEventListener("keydown", handleKeydown, true);
}

function disableKeyboardControls() {
	window.removeEventListener("keydown", handleKeydown, true);
}

function handleCanvasClick(event) {
	if (!sceneReady) return; // Ignore clicks if the scene isn't ready

	mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
	mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
	raycaster.setFromCamera(mouse, camera);

	const intersects = raycaster.intersectObjects(scene.children, true);

	if (intersects.length > 0) {
		let parent = intersects[0].object;

		while (parent && !("mixerIndex" in parent.userData) && parent.parent) {
			parent = parent.parent;
		}

		if (parent && "mixerIndex" in parent.userData) {
			const mixerIndex = parent.userData.mixerIndex;

			if (
				currentModelIndex !== undefined &&
				mixers[currentModelIndex] &&
				currentModelIndex !== mixerIndex
			) {
				mixers[currentModelIndex]._actions.forEach(
					(action) => (action.paused = true),
				);
			}

			const { position, lookAt } = predefinedCameraPositions[mixerIndex];

			gsap.to(".ui-container", {
				opacity: 0,
				duration: 0.5,
				display: "none",
				onComplete: () => {
					currentModelIndex = mixerIndex;
					disableKeyboardControls();

					controls.enabled = true;
					controls.enableDamping = true;
					controls.dampingFactor = 0.005;
					controls.enablePan = false;
					controls.enableRotate = true;
					controls.panSpeed = 0.75;

					function degToRad(degrees) {
						return (degrees * Math.PI) / 180;
					}

					controls.mouseButtons = {
						LEFT: THREE.MOUSE.ROTATE,
						RIGHT: THREE.MOUSE.PAN,
					};
					controls.touches = {
						ONE: THREE.TOUCH.ROTATE,
						TWO: THREE.TOUCH.PAN,
					};

					gsap.to(camera.position, {
						x: position.x,
						y: position.y,
						z: position.z,
						duration: 3.5,
						ease: "power1.inOut",
					});

					const currentLookAt = new THREE.Vector3();
					camera.getWorldDirection(currentLookAt);
					currentLookAt.add(camera.position);

					gsap.to(currentLookAt, {
						x: lookAt.x,
						y: lookAt.y,
						z: lookAt.z,
						duration: 2,
						ease: "power1.inOut",
						onUpdate: () => {
							camera.lookAt(currentLookAt);
							controls.target.copy(currentLookAt);
						},
						onComplete: () => {
							setTimeout(() => {
								if (mixers[mixerIndex]) {
									mixers[mixerIndex]._actions.forEach(
										(action) => (action.paused = false),
									);
								}

								gsap.to(".ui-container", {
									opacity: 1,
									duration: 0.5,
									display: "block",
								});
								showModelUI(mixerIndex);
								controls.minPolarAngle = degToRad(45);
								controls.maxPolarAngle = degToRad(80);
								controls.minAzimuthAngle = degToRad(0);
								controls.maxAzimuthAngle = degToRad(90);
								sceneReady = true;
							}, 500);
						},
					});
				},
			});
		}
	}
}

// Function to reset camera and hide UI
function resetCameraAndView() {
	if (mixers[currentModelIndex] && mixers[currentModelIndex]._actions[0]) {
		mixers[currentModelIndex]._actions[0].paused = true;
	}

	const { exitPosition, exitLookAt } = modelExitViews[currentModelIndex] || {};

	gsap.to(camera.position, {
		x: exitPosition.x,
		y: exitPosition.y,
		z: exitPosition.z,
		duration: 3,
		ease: "power1.inOut",
	});

	const newLookAt = new THREE.Vector3(exitLookAt.x, exitLookAt.y, exitLookAt.z);

	gsap.to(controls.target, {
		x: newLookAt.x,
		y: newLookAt.y,
		z: newLookAt.z,
		duration: 3.5,
		ease: "power1.inOut",
		onUpdate: () => {
			camera.lookAt(newLookAt);
			controls.enabled = false;
			controls.enableDamping = false;
		},
		onComplete: () => {
			hideModelUI();
			controls.enabled = true;
			controls.enablePan = true;
			controls.panSpeed = 0.75;
			controls.enableDamping = true;
			controls.dampingFactor = 0.025;
			controls.maxTargetRadius = 75;
			controls.enableRotate = false;
			controls.enableZoom = false;
			controls.zoomToCursor = false;
			controls.screenSpacePanning = false;

			controls.mouseButtons = {
				LEFT: THREE.MOUSE.PAN,
				RIGHT: THREE.MOUSE.ROTATE,
			};

			controls.touches = {
				ONE: THREE.TOUCH.PAN,
				TWO: THREE.TOUCH.ROTATE,
			};
			enableKeyboardControls(); // Re-enable keyboard controls here
			controls.update();
			sceneReady = true;
		},
	});
}

let currentModelIndex = 0; // Tracks the current model index

function changeModel(direction) {
	// Update current model index based on direction
	currentModelIndex += direction;
	if (currentModelIndex >= mixers.length) {
		currentModelIndex = 0; // Loop back to first model
	} else if (currentModelIndex < 0) {
		currentModelIndex = mixers.length - 1; // Loop back to last model
	}

	// Assuming predefinedCameraPositions are globally accessible
	const { position, lookAt } = predefinedCameraPositions[currentModelIndex];
	navigateToModel(position, lookAt, currentModelIndex);
}

function navigateToModel(position, lookAt, index) {
	// Check if there's a currently active model and its animation should be paused
	if (
		mixers[currentModelIndex] &&
		mixers[currentModelIndex]._actions.length > 0
	) {
		const currentAction = mixers[currentModelIndex]._actions[0];
		currentAction.paused = true; // Pause the current model's animation
	}

	gsap.to(".ui-container", { opacity: 0, duration: 0.5, display: "none" });

	// Animate camera to the new position smoothly
	gsap.to(camera.position, {
		x: position.x,
		y: position.y,
		z: position.z,
		duration: 2,
		ease: "power1.inOut",
		onComplete: () => {
			// After camera transition, update the current model index
			currentModelIndex = index;

			gsap.to(".ui-container", { opacity: 1, duration: 0.5, display: "block" });

			// Check if the new model has animations and play them
			if (mixers[index] && mixers[index]._actions.length > 0) {
				const newAction = mixers[index]._actions[0];
				newAction.paused = false; // Unpause the new model's animation
				newAction.play();
			}

			// Show UI for the new model
			showModelUI(index);
		},
	});

	// Smooth transition for 'lookAt' to ensure camera faces the new model correctly
	gsap.to(controls.target, {
		x: lookAt.x,
		y: lookAt.y,
		z: lookAt.z,
		duration: 2,
		ease: "power1.inOut",
		onUpdate: () => {
			camera.lookAt(new THREE.Vector3(lookAt.x, lookAt.y, lookAt.z));
		},
		onComplete: () => {
			controls.update(); // Ensure controls are updated to the new target
		},
	});
}

document.getElementById("next-model").addEventListener("click", () => {
	let nextIndex = (currentModelIndex + 1) % mixers.length; // Loop back to the first model after the last
	const { position, lookAt } = predefinedCameraPositions[nextIndex];
	navigateToModel(position, lookAt, nextIndex);
});

document.getElementById("prev-model").addEventListener("click", () => {
	let prevIndex = (currentModelIndex - 1 + mixers.length) % mixers.length; // Loop back to the last model before the first
	const { position, lookAt } = predefinedCameraPositions[prevIndex];
	navigateToModel(position, lookAt, prevIndex);
});

document
	.getElementById("exit-view")
	.addEventListener("click", resetCameraAndView);

canvas.addEventListener("click", handleCanvasClick);

canvas.addEventListener("mousemove", (event) => {
	// Convert the mouse position to normalized device coordinates (-1 to +1) for both axes
	mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
	mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

	// Update the raycaster with the camera and mouse position
	raycaster.setFromCamera(mouse, camera);

	// Calculate objects intersecting the picking ray. The second parameter `true` ensures all descendants are checked
	const intersects = raycaster.intersectObjects(scene.children, true);

	// Check if there's at least one object under the cursor
	if (intersects.length > 0) {
		let hoveredOverModel = false;

		// Check each intersected object to see if it's one of our models
		for (let i = 0; i < intersects.length; i++) {
			let parent = intersects[i].object;

			// Traverse up to find a parent object with userData.mixerIndex, indicating it's one of the models
			while (parent && !("mixerIndex" in parent.userData) && parent.parent) {
				parent = parent.parent;
			}

			// If we found a model, break out of the loop
			if (parent && "mixerIndex" in parent.userData) {
				hoveredOverModel = true;
				break;
			}
		}
		// Change the cursor style based on whether we're hovering over a model
		canvas.style.cursor = hoveredOverModel ? "pointer" : "move";
	} else {
		// No objects under the cursor, revert the cursor style
		canvas.style.cursor = "move";
	}
});

/**
 * Model Viewer
 */
document.getElementById("exit-view").addEventListener("click", function () {
	// GSAP or other logic to move the camera back
	// Hide UI elements
	gsap.to(".ui-container", {
		opacity: 0,
		duration: 0.5,
		onComplete: function () {
			document.querySelector(".ui-container").style.display = "none";
		},
	});
});

/**
 * Sizes and Fullscreen
 */
const sizes = {
	width: window.innerWidth,
	height: window.innerHeight,
};

window.addEventListener("resize", () => {
	// Update sizes
	sizes.width = window.innerWidth;
	sizes.height = window.innerHeight;

	// Update camera
	camera.aspect = sizes.width / sizes.height;
	camera.updateProjectionMatrix();

	// Update renderer
	renderer.setSize(sizes.width, sizes.height);
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

window.addEventListener("dblclick", () => {
	const fullscreenElement =
		document.fullscreenElement || document.webkitFullscreenElement;

	if (!fullscreenElement) {
		if (document.body.requestFullscreen) {
			document.body.requestFullscreen();
		} else if (document.body.webkitRequestFullscreen) {
			document.body.webkitRequestFullscreen;
		}
	} else {
		if (document.exitFullscreen) {
			document.exitFullscreen();
		} else if (document.webkitExitFullscreen) {
			document.webkitExitFullscreen();
		}
	}
});

// Listen for fullscreen change events (for most browsers)
document.addEventListener("fullscreenchange", updateFullscreenClass);
// WebKit browsers (older versions of Safari etc.)
document.addEventListener("webkitfullscreenchange", updateFullscreenClass);

function updateFullscreenClass() {
	if (document.fullscreenElement || document.webkitFullscreenElement) {
		document.body.classList.add("is-fullscreen");
	} else {
		document.body.classList.remove("is-fullscreen");
	}
}

/**
 * Camera
 */
// Base camera

// Perspective camera
const camera = new THREE.PerspectiveCamera(
	45,
	sizes.width / sizes.height,
	0.1,
	400,
);
// camera.position.set(0,150,0) // Top View
// camera.position.set(150,50,0) // Top Right
// camera.position.set(-115,50,75) // Top Left
// camera.position.set(0,50,100) // Top Left
// camera.position.set(75,75,75) // Top Opposite Scene Angle
// camera.position.set(-75,75,-75) // Top Scene Angle
camera.position.set(20, 25, 20); // Default View
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enabled = true;
controls.enableDamping = true;
controls.enablePan = true;
controls.dampingFactor = 0.025;
controls.maxTargetRadius = 75;
controls.enableRotate = false;
controls.enableZoom = false;
controls.zoomToCursor = false;
controls.screenSpacePanning = false;
controls.panSpeed = 0.75;

// Mouse Controls
controls.mouseButtons = {
	LEFT: THREE.MOUSE.PAN,
	RIGHT: THREE.MOUSE.ROTATE,
};

// Touch Controls
controls.touches = {
	ONE: THREE.TOUCH.PAN,
	TWO: THREE.TOUCH.ROTATE,
};

// Controls Target
const maincameraTarget = new THREE.Vector3(0, 0, 0);
controls.target = maincameraTarget;

// /**
//  * Fog
//  */
const fog = new THREE.Fog("#95B0AE", 50, 80);
scene.fog = fog;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
	canvas: canvas,
	antialias: true,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setClearColor("#95B0AE"); // Set clear color with gamma correction in mind

// Shadow Map
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap;

/**
 * Animate
 */
const clock = new THREE.Clock();
let previousTime = 0;

const tick = () => {
	const elapsedTime = clock.getElapsedTime();
	const deltaTime = elapsedTime - previousTime;
	previousTime = elapsedTime;

	// Update specific mixer for scene.glb
	if (sceneMixer) {
		sceneMixer.update(deltaTime);
	}
	if (flagMixer) {
		flagMixer.update(deltaTime);
	}

	// Model animation
	mixers.forEach((mixer) => mixer && mixer.update(deltaTime)); // Update all mixers

	// Rotate the wheel model around its Y-axis
	wheelModels.forEach((wheel) => {
		wheel.rotation.x += 0.1 * deltaTime; // Adjust rotation speed here
	});

	if (sceneWheelModel01 && sceneWheelModel02 && sceneWheelModel03) {
		sceneWheelModel01.rotation.x += 1 * deltaTime; // Adjust multiplier as needed for speed
		sceneWheelModel02.rotation.x += 1 * deltaTime; // Ensure consistent animation speed
		sceneWheelModel03.rotation.x += 1 * deltaTime; // Ensure consistent animation speed
	}

	// Oscillate glowStrength smoothly between 0.02 and 0.10
	const minGlowStrength = 0.02;
	const maxGlowStrength = 0.1;
	const glowStrength =
		minGlowStrength +
		(Math.sin(elapsedTime) * 0.3 + 0.3) * (maxGlowStrength - minGlowStrength);

	// Animate the water normal map by modifying its offset
	if (waterNormalsTexture) {
		waterNormalsTexture.offset.x += 0.04 * deltaTime;
	}

	// Update the shader uniform
	gridGlowMaterial.uniforms.glowStrength.value = glowStrength;
	holoMaterial.uniforms.uTime.value = elapsedTime;

	// Update controls
	controls.update();

	// Render
	renderer.render(scene, camera);

	// Call tick again on the next frame
	window.requestAnimationFrame(tick);
};

tick();
